import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { SpaceTab } from "../../shared/mutators/markSpaceTabViewed";
import { namesMatch, normalizeName } from "../../shared/normalizeName";
import { slugifyName, slugValidationError } from "../../shared/slug";
import { queries } from "../../shared/queries";
import { defaultCreatorGrants, type GrantEntry } from "../../shared/rbac/roles";
import { useHasAccess } from "../hooks/useHasAccess";
import { useViewVisit } from "../hooks/useViewVisit";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useSpaces } from "../hooks/useSpaces";
import { useSessionTenantEdgeUrlOptions } from "../hooks/useSessionTenantOrigin";
import { maxSpaceSlugLength } from "../../shared/edgeUrl";
import { spaceIcon } from "../lib/spaceIcon";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../lib/urls";
import { closeModal } from "./globalModalStack";
import { AccessMapPanel } from "./AccessMapPanel";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { useScopeAccessCloseWarning } from "./ScopeAccessCloseWarning";
import { HeaderIconContainer } from "./SettingsLayout";
import {
  type DiscoverabilityValue,
  SpaceSettingsForm,
  type SpaceSettingsTab,
} from "./SpaceSettingsForm";
import { TabbedModal } from "./TabbedModal";

type Props =
  | { mode: "edit"; spaceId: string; onClose?: () => void }
  | {
      mode: "create";
      onCreated: (spaceId: string, name: string) => void;
      onClose: () => void;
    };

const TABS = [
  { key: "settings", label: "Settings", icon: "settings-2" },
  { key: "access", label: "Access", icon: "lock" },
] as const;

export function SpaceSettings(props: Props) {
  if (props.mode === "create") {
    return (
      <CreateSpaceSettings
        onCreated={props.onCreated}
        onClose={props.onClose}
      />
    );
  }
  return <EditSpaceSettings spaceId={props.spaceId} onClose={props.onClose} />;
}

function CreateSpaceSettings({
  onCreated,
  onClose,
}: {
  onCreated: (spaceId: string, name: string) => void;
  onClose: () => void;
}) {
  const [currentUser] = useQuery(queries.currentUser());
  if (!currentUser) return null;
  return (
    <CreateSpaceSettingsInner
      currentUserId={currentUser.id}
      onCreated={onCreated}
      onClose={onClose}
    />
  );
}

function CreateSpaceSettingsInner({
  currentUserId,
  onCreated,
  onClose,
}: {
  currentUserId: string;
  onCreated: (spaceId: string, name: string) => void;
  onClose: () => void;
}) {
  const z = useZero();
  const [spaceId] = useState(() => nanoid(12));
  const [name, setName] = useState("");
  const [slugOverride, setSlugOverride] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantEntry[]>(() =>
    defaultCreatorGrants("space", { userId: currentUserId })
  );
  const [discoverability, setDiscoverability] = useState<DiscoverabilityValue>({
    defaultJoinBuiltinRoleId: null,
    defaultJoinCustomRoleId: null,
  });
  const [activeTab, setActiveTab] = useState<SpaceSettingsTab>("settings");
  const [allSpaces] = useSpaces({ settingsView: false });
  const maxSlugLength = maxSpaceSlugLength(useSessionTenantEdgeUrlOptions());
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  const normalized = normalizeName(name);
  const nameTaken =
    normalized.length > 0 &&
    (allSpaces ?? []).some((s) => namesMatch(s.name, normalized));

  const derivedSlug = slugifyName(normalized);
  // An empty override means "fall back to the derived slug", so clearing the
  // field always returns the user to a valid state rather than trapping them.
  const customSlug =
    slugOverride !== null && slugOverride !== "" ? slugOverride : null;
  const slug = customSlug ?? derivedSlug;
  // Validate the effective slug, not just a custom one, so a name that derives
  // a reserved/invalid slug (e.g. "API" → "api") is caught inline rather than
  // only when the server rejects createSpace.
  const slugError =
    slug.length > 0 ? slugValidationError(slug, maxSlugLength) : null;
  const slugTaken =
    slug.length > 0 && (allSpaces ?? []).some((s) => s.slug === slug);

  const canCreate =
    normalized.length > 0 && !nameTaken && slugError === null && !slugTaken;

  const handleCreate = (grantsOverride?: GrantEntry[]) => {
    if (!canCreate) return;
    z.mutate(
      sharedMutators.createSpace({
        id: spaceId,
        name: normalized,
        ...(customSlug !== null && { slug: customSlug }),
        mode: "private",
        grants: grantsOverride ?? grants,
        defaultJoinBuiltinRoleId: discoverability.defaultJoinBuiltinRoleId,
        defaultJoinCustomRoleId: discoverability.defaultJoinCustomRoleId,
      })
    );
    onCreated(spaceId, normalized);
  };

  const close = () => requestClose(onClose);
  const create = () => requestClose(handleCreate);

  return (
    <>
      <TabbedModal
        icon={
          <HeaderIconContainer>
            <Icon name="hash" size={16} />
          </HeaderIconContainer>
        }
        title="Create space"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onClose={close}
        footerRight={
          activeTab === "settings" ? (
            <Button
              variant="primary"
              size="l"
              onClick={() => setActiveTab("access")}
              disabled={!canCreate}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              size="l"
              onClick={create}
              disabled={!canCreate}
            >
              Create
            </Button>
          )
        }
      >
        <SpaceSettingsForm
          mode="create"
          name={name}
          setName={setName}
          slug={slug}
          setSlug={setSlugOverride}
          slugError={slugError}
          slugTaken={slugTaken}
          grants={grants}
          setGrants={setGrants}
          discoverability={discoverability}
          setDiscoverability={setDiscoverability}
          activeTab={activeTab}
          stagedGrantsRef={stagedGrantsRef}
        />
      </TabbedModal>
      {closeWarningModal}
    </>
  );
}

function EditSpaceSettings({
  spaceId,
  onClose,
}: {
  spaceId: string;
  onClose?: () => void;
}) {
  const makeUrl = useMakeUrl();
  const rbacCanManageSpace = useHasAccess("space:manage", spaceId);
  const canManageSpace = rbacCanManageSpace;
  const [space] = useQuery(queries.navSpace({ spaceId }));
  const displaySpaceName = useSpaceDisplayName();
  const dismiss = onClose ?? (() => closeModal());
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });
  const { tab } = useCurrentUrl(routes.modal.space, { id: spaceId });
  const activeTab = tab as SpaceSettingsTab;
  const z = useZero();
  useViewVisit({
    visitKey: spaceId,
    tab: TABS.some((t) => t.key === tab) ? activeTab : null,
    onView: ({ visitId, navSource, priorTab }) =>
      z.mutate(
        sharedMutators.markSpaceTabViewed({
          spaceId,
          tab: activeTab,
          visitId,
          navSource,
          priorTab: priorTab as SpaceTab | null,
        })
      ),
  });

  const headerIcon = space ? spaceIcon(space.mode) : "hash";
  const headerTitle = space ? displaySpaceName(space) : "Space";

  const close = () => requestClose(dismiss);

  return (
    <>
      <TabbedModal
        icon={
          <HeaderIconContainer>
            <Icon name={headerIcon} size={16} />
          </HeaderIconContainer>
        }
        title={headerTitle}
        tabs={TABS.map((t) => ({
          ...t,
          to: makeUrl(routes.modal.space, { id: spaceId, tab: t.key }),
        }))}
        activeTab={activeTab}
        onTabChange={(next) =>
          navigate(routes.modal.space, { id: spaceId, tab: next })
        }
        onClose={close}
        footerRight={
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="subtle"
              size="l"
              onClick={() =>
                requestClose(() => {
                  dismiss();
                  navigate(routes.space, { spaceId });
                })
              }
            >
              View space
            </Button>
            <Button variant="primary" size="l" onClick={close}>
              Done
            </Button>
          </div>
        }
      >
        <SpaceSettingsForm
          mode="edit"
          spaceId={spaceId}
          canManageSpace={canManageSpace}
          activeTab={activeTab}
          stagedGrantsRef={stagedGrantsRef}
          onDeleted={dismiss}
          accessMapPanel={
            space && space.mode !== "personal" ? (
              <AccessMapPanel
                nodeId={spaceId}
                noun="space"
                guard={requestClose}
              />
            ) : undefined
          }
        />
      </TabbedModal>
      {closeWarningModal}
    </>
  );
}
